<script setup lang="ts">
import { AbcDropdown } from "@ordbogen-npm/abc-dsk";
import { debounce } from "lodash-es";
import { storeToRefs } from "pinia";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import type { LocationQueryRaw } from "vue-router";
import { useRoute, useRouter } from "vue-router";

import TgSelectorButton from "@/abc-dsk/components/SelectorButton/TgSelectorButton.vue";
import TgSelectorButtonGroup from "@/abc-dsk/components/SelectorButton/TgSelectorButtonGroup.vue";
import { useArticleTabs } from "@/common/composables/use-article-tab";
import { BREAKPOINTS } from "@/common/config";
import Dictionaries from "@/common/dictionaries";
import { LANGUAGE_GROUP_ARABIC, LANGUAGE_GROUP_RUSSIAN } from "@/common/language-groups";
import RouteHelper from "@/common/route-helper";
import { SEARCH_DEBOUNCE_TIME } from "@/common/search";
import WordCompletion from "@/components/searchBar/WordCompletion.vue";
import { MatomoHelper } from "@/helpers";
import {
  QUERY_SEARCH_DICTIONARY_KEY,
  QUERY_SEARCH_DIRECTION_KEY,
  QUERY_SEARCH_FORCE_DIRECTION_KEY,
  QUERY_SEARCH_WORD_KEY
} from "@/router/query-params";
import { ROUTER_PARAM_LANGUAGE, ROUTER_PARAM_PACKAGE_GROUP } from "@/router/router-params";
import { useSearchStore, useSettingsStore } from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

import KeyBoards from "./KeyBoards.vue";
import SearchBarBase from "./SearchBarBase.vue";

interface Dictionary {
  methodId: string;
  dictionaryId: string;
}

const props = defineProps<{
  scrollTarget?: string;
}>();

const router = useRouter();
const route = useRoute();

const { preferredPackageGroup, activeLanguageGroup } = storeToRefs(useSettingsStore());
const searchStore = useSearchStore();

const { t } = useI18n();

const articleTabs = useArticleTabs(router);
const routeHelper = new RouteHelper(router);

const searchDictionaryId = computed(() => searchStore.searchDictionaryId);
const searchDirection = computed(() => searchStore.searchDirection);
const searchDirectionsAvailable = computed(() => searchStore.searchDirectionsAvailable);
const stateSearchWord = computed(() => searchStore.searchWord);
const isSearching = computed(() => searchStore.searchState === "loading");

const searchWord = computed({
  get() {
    return stateSearchWord.value;
  },
  set(value) {
    getWordCompletionList(value);
    searchStore.searchWord = value;
  }
});

const isSearchDisabled = computed(() => searchWord.value.trim() === "");

const searchWordTrimmed = computed(() => searchWord.value.trim());
const scrollLocation = computed(() => (props.scrollTarget ? `#${props.scrollTarget}` : ""));
const useDropdown = computed(
  () =>
    searchDictionaryId.value &&
    Dictionaries.getDropdownSupportByDictionaryId(searchDictionaryId.value)
);
const useDirections = computed(
  () =>
    searchDirectionsAvailable.value.length > 1 &&
    searchDirection.value &&
    Dictionaries.getDirectionsSupportByDirection(searchDirection.value)
);
const useDictionaryLabel = computed<boolean>(() =>
  Boolean(
    searchDictionaryId.value &&
      Dictionaries.getDictionaryLabelSupportByDictionaryId(searchDictionaryId.value)
  )
);

const showKeyboard = computed<boolean>(() =>
  [LANGUAGE_GROUP_ARABIC, LANGUAGE_GROUP_RUSSIAN].includes(activeLanguageGroup.value ?? "")
);

const useAutofocus = computed(() => route.name !== "article");

const dropdownDictionaries = computed(() => {
  if (!searchDirection.value) {
    return [];
  }
  return (Dictionaries.getDictionariesByDirection(searchDirection.value) as Dictionary[]).map(
    (dictionary) => ({
      value: dictionary.dictionaryId,
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      label: t(`article.dropdownDictionaries.${dictionary.dictionaryId}`)
    })
  );
});

const dictionaryLabel = computed<string>(() => {
  if (!currentDictionary.value) {
    return "";
  }

  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(`article.dropdownDictionaries.${currentDictionary.value.dictionaryId}`);
});

const currentDictionary = computed(() => {
  if (!searchDictionaryId.value) {
    return undefined;
  }

  return Dictionaries.getDictionaryByDictionaryId(searchDictionaryId.value);
});

const voiceSearchLanguage = computed(() => searchDirection.value?.substring(0, 2));

const dropdownValue = computed({
  get() {
    return currentDictionary.value?.methodId ?? "";
  },
  set(value) {
    if (searchDictionaryId.value !== value) {
      MatomoHelper.sendEvent(
        MatomoEventCategory.Search,
        MatomoEventAction.ClickSearchbarIcons,
        "Clicked dictionary in dropdown"
      );
    }

    searchStore.searchDictionaryId = value;
  }
});

onMounted(() => {
  if (searchDirection.value) {
    searchStore.resetSearchDirectionsAvailable();
  } else {
    searchStore.resetSearchParameters();
  }
});

const getWordCompletionList = debounce(function (value: string) {
  return searchStore.wordCompletion(value);
}, SEARCH_DEBOUNCE_TIME);

const resetWordCompletionOnAutoFocus = () => {
  searchStore.wordCompletionList = [];
};

const onDirectionSwitch = (direction: string) => {
  const oldDirection = searchDirection.value;
  searchStore.searchDirection = direction;

  const queryParams = routeHelper.getQueryParams({}) as Record<string, string>;
  const querySearchWord = queryParams?.[QUERY_SEARCH_WORD_KEY] || "";

  if (oldDirection === searchDirection.value && searchWordTrimmed.value === querySearchWord) {
    return;
  }

  onSearch(true);

  MatomoHelper.sendEvent(
    MatomoEventCategory.Search,
    MatomoEventAction.ClickSearchbarIcons,
    "Clicked language direction in opposite direction"
  );
};

const onSearch = async (forcedDirectionSearch = false) => {
  if (searchWordTrimmed.value === "") {
    return;
  }

  const scrollLocationAnchor = window.innerWidth < BREAKPOINTS.lg ? scrollLocation.value : "";

  const nextQuery = articleTabs.changeArticleTab(
    undefined,
    {
      [QUERY_SEARCH_WORD_KEY]: searchWord.value,
      [QUERY_SEARCH_DICTIONARY_KEY]: searchDictionaryId.value,
      [QUERY_SEARCH_DIRECTION_KEY]: searchDirection.value,
      [QUERY_SEARCH_FORCE_DIRECTION_KEY]: undefined
    },
    true
  ) as LocationQueryRaw;

  const params = {
    [ROUTER_PARAM_PACKAGE_GROUP]: preferredPackageGroup.value?.id,
    [ROUTER_PARAM_LANGUAGE]: activeLanguageGroup.value
  };

  const newRoute = {
    name: "article",
    query: nextQuery,
    hash: scrollLocationAnchor,
    params: params
  };
  const newHref = router.resolve(newRoute).href;
  const currentHref = route.fullPath;

  if (newHref !== currentHref) {
    if (forcedDirectionSearch) {
      newRoute.query[QUERY_SEARCH_FORCE_DIRECTION_KEY] = 1;
    }

    await router.push(newRoute);
  }
};

const trackSearchbarIconClick = () => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.Search,
    MatomoEventAction.ClickSearchbarIcons,
    "Clicked search icon"
  );
};

const trackLanguageDirectionClick = () => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.Search,
    MatomoEventAction.ClickSearchbarIcons,
    "Clicked language direction"
  );
};
</script>

<template>
  <SearchBarBase
    v-model:query="searchWord"
    :auto-focus="useAutofocus"
    :disabled="isSearchDisabled"
    :voice-search-language="voiceSearchLanguage"
    class="search-bar"
    @search="onSearch()"
    @auto-focus-reset="resetWordCompletionOnAutoFocus()"
    @click-search-icon="trackSearchbarIconClick()"
  >
    <template #word-completion="{ hasFocus }">
      <WordCompletion :search-input-focus="hasFocus" @select-result="onSearch()" />
    </template>
    <template #actions>
      <KeyBoards
        v-if="showKeyboard"
        class="d-none d-lg-block"
        @key="searchWord = searchWord + $event"
        @backspace="searchWord = searchWord.slice(0, -1)"
      />

      <AbcDropdown
        v-if="useDropdown"
        v-model="dropdownValue"
        :aria-label="t('searchBar.dropdownLabel')"
        color="primary"
        class="search-bar__dropdown search-bar__selector"
        :options="dropdownDictionaries"
        :disabled="isSearching"
        @close="onSearch()"
      />
      <TgSelectorButtonGroup
        v-if="useDirections"
        v-slot="{ state, handleBlur, handleFocusNext, handleFocusPrev, handleSelect }"
        :selected="searchDirection"
        :items="searchDirectionsAvailable"
        class="search-bar__group search-bar__group--fit-content-lg search-bar__selector"
        @input="onDirectionSwitch($event.item)"
        @click="trackLanguageDirectionClick()"
      >
        <TgSelectorButton
          v-for="(direction, index) in searchDirectionsAvailable"
          :key="direction"
          :index="index"
          :tabbable="state.tabbableIndex === index"
          :selected="state.selectedIndex === index"
          :disabled="isSearching"
          :class="[
            'search-bar__group',
            'search-bar__group--space-left-lg',
            'search-bar__group--fit-content-lg',
            index !== 0 ? 'search-bar__selector' : ''
          ]"
          :aria-label="
            // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
            t(`searchBar.searchDirections.${direction}.buttonAriaLabel`)
          "
          @blur="handleBlur($event)"
          @select="handleSelect($event)"
          @focus-next="handleFocusNext($event)"
          @focus-prev="handleFocusPrev($event)"
        >
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
          {{ t(`searchBar.searchDirections.${direction}.buttonLabel`) }}
        </TgSelectorButton>
      </TgSelectorButtonGroup>
      <div v-if="useDictionaryLabel" class="search-bar__dictionary-label">
        <span class="search-bar__dictionary-label-text">{{ dictionaryLabel }}</span>
      </div>
    </template>
  </SearchBarBase>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.search-bar {
  &__dropdown {
    max-height: 47px;
    font-size: 0.81rem;

    #media-breakpoint-up(lg, {
      margin-left: 10px;
    });
  }

  &__selector {
    margin-top: 9.6px;

    #media-breakpoint-up(lg, {
      margin-top: 0;
    });
  }

  &__group {
    width: 100%;

    &--space-left-lg {
      #media-breakpoint-up(lg, {
        margin-left: 9.6px;
      });
    }

    &--fit-content-lg {
      #media-breakpoint-up(lg, {
        width: auto;
      });
    }
  }

  &__dictionary-label {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-height: 48px;
    margin-top: 8px;
    padding: 10px 14px;
    overflow: hidden;
    border: 1px solid rgb(@theme-colors[@primary][base]);
    border-radius: 4px;
    background: rgb(@theme-colors[@primary][base]);
    color: @colors[@white];
    font-weight: 500;
    pointer-events: none;

    #media-breakpoint-up(lg, {
      justify-content: start;
      width: 320px;
      margin-top: 0;
      margin-left: 8px;
    });
  }
}
</style>
